import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useRouteGeometry, type RouteGeometryData } from '../../hooks/useRouteGeometry'
import { TripRoute3DPlayerModal } from './TripRoute3DPlayerModal'
import { TripWaypointFlythrough } from './TripWaypointFlythrough'
import { Sparkles, Lock, Film } from '@/ui/icons'
import { cn } from '@/shared/utils'
import {
  cumulativeDistances,
  interpolateAlongLine,
  bearingBetween,
} from '../../utils/routeGeo'

const ROUTE_LINE_COLOR = '#22c55e'
const ROUTE_CASING_COLOR = '#000000'
const MARKER_START_COLOR = '#22c55e'
const MARKER_END_COLOR = '#5b8fbe'
const MARKER_MID_COLOR = '#6b7280'

const STYLE_URL = 'mapbox://styles/ayott/cmdldmyxv00eu01ptcd7acb7k'
const TEASER_DURATION_MS = 4000

interface TripRoute3DMapViewProps {
  geometry: RouteGeometryData
  onMapReady?: (map: mapboxgl.Map) => void
}

function TripRoute3DMapView({ geometry, onMapReady }: TripRoute3DMapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const coords = geometry.coordinates
    if (coords.length < 2) return

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN

    const mid = coords[Math.floor(coords.length / 2)]

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: [mid[0], mid[1]],
      zoom: 8,
      pitch: 35,
      bearing: 0,
      attributionControl: false,
      logoPosition: 'bottom-right',
    })
    mapRef.current = map

    map.on('load', () => {
      const geoJson: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: coords },
      }

      map.addSource('route-3d-preview', { type: 'geojson', data: geoJson })

      map.addLayer({
        id: 'route-3d-casing',
        type: 'line',
        source: 'route-3d-preview',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ROUTE_CASING_COLOR,
          'line-width': 8,
          'line-opacity': 0.55,
        },
      })

      map.addLayer({
        id: 'route-3d-line',
        type: 'line',
        source: 'route-3d-preview',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ROUTE_LINE_COLOR,
          'line-width': 5,
        },
      })

      const waypointFeatures: GeoJSON.Feature<GeoJSON.Point>[] = geometry.waypoints.map((wp, i) => ({
        type: 'Feature',
        properties: {
          color:
            i === 0
              ? MARKER_START_COLOR
              : i === geometry.waypoints.length - 1
                ? MARKER_END_COLOR
                : MARKER_MID_COLOR,
        },
        geometry: { type: 'Point', coordinates: [wp.longitude, wp.latitude] },
      }))

      map.addSource('waypoints-3d-preview', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: waypointFeatures },
      })

      map.addLayer({
        id: 'waypoints-3d-stroke',
        type: 'circle',
        source: 'waypoints-3d-preview',
        paint: { 'circle-radius': 8, 'circle-color': '#ffffff', 'circle-opacity': 1 },
      })

      map.addLayer({
        id: 'waypoints-3d-fill',
        type: 'circle',
        source: 'waypoints-3d-preview',
        paint: { 'circle-radius': 6, 'circle-color': ['get', 'color'] },
      })

      const lngs = coords.map((c) => c[0])
      const lats = coords.map((c) => c[1])
      const bounds = new mapboxgl.LngLatBounds(
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      )
      map.fitBounds(bounds, { padding: 48, pitch: 35, bearing: 0, duration: 700 })

      onMapReady?.(map)
    })

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- intentional: initialize once

  return <div ref={containerRef} className="w-full h-full" />
}

// ─── Teaser flyover ───────────────────────────────────────────────────────────

function useTeaserFlyover(
  map: mapboxgl.Map | null,
  coords: [number, number][],
  enabled: boolean,
  onDone: () => void,
) {
  useEffect(() => {
    if (!enabled || !map || coords.length < 2) return

    const cumDists = cumulativeDistances(coords)
    const startTime = performance.now()
    // Travel the first 20% of the route during the teaser
    const TEASER_ROUTE_FRACTION = 0.20
    let rafId: number

    function frame(now: number) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / TEASER_DURATION_MS, 1)

      const t = progress * TEASER_ROUTE_FRACTION
      const pos = interpolateAlongLine(coords, cumDists, t)
      const ahead = interpolateAlongLine(coords, cumDists, Math.min(t + 0.01, 1))
      const bearing = bearingBetween(pos, ahead)

      map!.easeTo({
        center: pos,
        bearing,
        pitch: 55,
        zoom: 13,
        duration: 80,
        easing: (x) => x,
      })

      if (progress < 1) {
        rafId = requestAnimationFrame(frame)
      } else {
        onDone()
      }
    }

    // Small delay to let the fitBounds animation settle first
    const timeout = setTimeout(() => {
      rafId = requestAnimationFrame(frame)
    }, 900)

    return () => {
      clearTimeout(timeout)
      cancelAnimationFrame(rafId)
    }
  }, [map, enabled]) // eslint-disable-line react-hooks/exhaustive-deps -- intentional: run once when enabled+map are ready
}

// ─── Main component ───────────────────────────────────────────────────────────

interface TripRoute3DPreviewProps {
  tripId: string
  fallback: React.ReactNode
  className?: string
}

export function TripRoute3DPreview({ tripId, fallback, className }: TripRoute3DPreviewProps) {
  const { data: geometry, isLoading } = useRouteGeometry(tripId)
  const [showPlayer, setShowPlayer] = useState(false)
  const [showFlythrough, setShowFlythrough] = useState(false)
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null)
  const [teaserDone, setTeaserDone] = useState(false)

  const isPreviewOnly = geometry?.previewOnly ?? false

  useTeaserFlyover(
    mapInstance,
    geometry?.coordinates ?? [],
    isPreviewOnly && mapInstance !== null,
    () => setTeaserDone(true),
  )

  // No geometry and not loading → no waypoints yet, show flat map as-is
  if (!isLoading && !geometry) {
    return <>{fallback}</>
  }

  const distanceMiles = geometry ? geometry.distanceMeters / 1609.344 : 0
  const stopCount = geometry ? geometry.waypoints.length : 0
  const milesLabel =
    distanceMiles >= 100
      ? `${Math.round(distanceMiles)} mi`
      : `${distanceMiles.toFixed(1)} mi`
  const statsLabel = `${stopCount} ${stopCount === 1 ? 'stop' : 'stops'} · ${milesLabel}`

  return (
    <>
      <div className={cn('relative w-full h-full overflow-hidden rounded-lg', className)}>
        {/* Map — always rendered when geometry exists */}
        {geometry ? (
          <TripRoute3DMapView geometry={geometry} onMapReady={setMapInstance} />
        ) : (
          <div className="w-full h-full">{fallback}</div>
        )}

        {/* Loading shimmer */}
        {isLoading && (
          <div className="absolute inset-0 bg-earth-100 dark:bg-earth-900 animate-pulse rounded-lg" />
        )}

        {/* 3D chip — top left */}
        {geometry && !isLoading && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 pointer-events-none">
            <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            <span className="text-[11px] font-bold text-white tracking-wide">3D</span>
          </div>
        )}

        {/* Stats chip — bottom left */}
        {geometry && !isLoading && (
          <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-black/55 pointer-events-none">
            <span className="text-[11px] font-bold text-white tracking-wide">{statsLabel}</span>
          </div>
        )}

        {/* Pro user: action buttons */}
        {geometry && !isLoading && !isPreviewOnly && (
          <div className="absolute bottom-3 right-3 flex items-center gap-2">
            <button
              onClick={() => setShowFlythrough(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/55 text-white text-[12px] font-bold shadow hover:bg-black/70 transition-colors"
            >
              <Film className="w-3.5 h-3.5" />
              Flythrough
            </button>
            <button
              onClick={() => setShowPlayer(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-[13px] font-bold shadow-md hover:opacity-90 transition-opacity"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Play 3D Tour
            </button>
          </div>
        )}

        {/* Free user: soft upgrade banner — fades in after teaser */}
        {isPreviewOnly && teaserDone && geometry && !isLoading && (
          <div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 w-[82%] max-w-xs animate-in fade-in slide-in-from-bottom-2 duration-500"
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white/95 dark:bg-earth-900/95 shadow-xl border border-white/20 dark:border-white/10">
              <div className="flex items-center gap-2 min-w-0">
                <Lock className="h-4 w-4 text-adventure-600 dark:text-adventure-400 shrink-0" />
                <p className="text-xs font-semibold text-earth-900 dark:text-foreground truncate">
                  Full 3D Tour is a Pro feature
                </p>
              </div>
              <a
                href="/pricing"
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-adventure-600 hover:bg-adventure-700 text-white text-xs font-bold transition-colors shrink-0"
              >
                <Sparkles className="h-3 w-3" />
                Upgrade
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen player modal — Pro/Team only */}
      {showPlayer && geometry && !isPreviewOnly && (
        <TripRoute3DPlayerModal
          geometry={geometry}
          onClose={() => setShowPlayer(false)}
        />
      )}

      {/* Waypoint flythrough modal — Pro/Team only */}
      {showFlythrough && geometry && !isPreviewOnly && (
        <TripWaypointFlythrough
          waypoints={geometry.waypoints}
          onClose={() => setShowFlythrough(false)}
        />
      )}
    </>
  )
}
